import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import Header from './Header';
import Footer from './footer/Footer';
import GoogleReviews from './GoogleReviews';
import Faq from './Faq';
import { breadcrumbSchema, cityServiceSchema, localBusinessSchema } from '../data/schema';
import '../css/CityPage.css';

const citySlugMap = {
    "Deauville": "",
    "Touques": "debarras-touques",
    "Trouville-sur-Mer": "debarras-trouville-sur-mer",
    "Tourgéville": "debarras-tourgeville",
    "Villers-sur-Mer": "debarras-villers-sur-mer",
    "Saint-Arnoult": "debarras-saint-arnoult",
    "Bénerville-sur-Mer": "debarras-benerville-sur-mer",
    "Blonville-sur-Mer": "debarras-blonville-sur-mer",
    "Villerville": "debarras-villerville",
    "Houlgate": "debarras-houlgate",
    "Dives-sur-Mer": "debarras-dives-sur-mer",
    "Auberville": "debarras-auberville",
};

const carouselImages = [
    {
        src: '/img/home/before_after_2026_may_deauville.png',
        alt: 'Débarras mai 2026 Deauville',
        title: 'Mai 2026, Deauville'
    },
    {
        src: '/img/home/before_after_2026_february_deauville.png',
        alt: 'Débarras février 2026 Deauville',
        title: 'Février 2026, Deauville'
    },
    {
        src: '/img/home/before_after_2026_january_deauville.png',
        alt: 'Débarras janvier 2026 Deauville',
        title: 'Janvier 2026, Deauville'
    },
    {
        src: '/img/home/before_after_2026_january_trouville.png',
        alt: 'Débarras janvier 2026 Trouville-sur-Mer',
        title: 'Janvier 2026, Trouville-sur-Mer'
    },
    {
        src: '/img/home/before_after_2025_october_deauville.png',
        alt: 'Débarras octobre 2025 Les Marinas Deauville',
        title: 'Octobre 2025, Les Marinas Deauville'
    },
    {
        src: '/img/home/before_after_2025_april_tourgeville.png',
        alt: 'Débarras avril 2025 Tourgéville',
        title: 'Avril 2025, Tourgéville'
    }
];

const CityPage = ({ city }) => {
    const { name, slug, postalCode, intro, nearby } = city;
    const canonicalUrl = `https://ccdebarras14.fr/debarras-${slug}`;
    const pageTitle = `Débarras ${name} (${postalCode}) gratuit | CC Débarras 14`;
    const metaDescription = `Débarras de maison, appartement, cave ou grenier à ${name} (${postalCode}). Succession, vente, départ en maison de retraite. Gratuit si vos objets sont revendables, sinon devis gratuit. ☎ 07 67 56 72 54`;

    const faqItems = [
        {
            question: `Combien coûte un débarras à ${name} ?`,
            answer: `Le débarras est gratuit à ${name} lorsque les objets récupérés peuvent être revendus. Si rien n'est vendable, nous établissons un devis clair, gratuit et sans engagement pour l'enlèvement, le tri et le dépôt en déchetterie.`,
        },
        {
            question: `Sous quel délai pouvez-vous intervenir à ${name} ?`,
            answer: `Nous nous déplaçons généralement sous 48 heures à ${name} (${postalCode}) pour établir le devis, et réalisons le débarras sous une semaine selon l'urgence, du lundi au samedi de 8h à 18h.`,
        },
        {
            question: `Que débarrassez-vous à ${name} ?`,
            answer: `Maisons, appartements, résidences secondaires, caves, greniers, sous-sols, garages, bureaux et locaux professionnels, notamment après une succession, avant une vente immobilière ou lors d'un départ en maison de retraite.`,
        },
        {
            question: `Intervenez-vous autour de ${name} ?`,
            answer: `Oui, nous intervenons aussi dans les communes voisines : ${nearby.join(', ')}, ainsi que sur toute la Côte Fleurie depuis Deauville.`,
        },
    ];

    const [currentSlide, setCurrentSlide] = useState(0);

    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentSlide((prev) => (prev + 1) % carouselImages.length);
        }, 4000);
        return () => clearInterval(interval);
    }, []);

    const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % carouselImages.length);
    const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + carouselImages.length) % carouselImages.length);

    return (
        <>
            <Helmet>
                <title>{pageTitle}</title>
                <meta name="description" content={metaDescription} />
                <link rel="canonical" href={canonicalUrl} />

                <meta property="og:title" content={`Débarras ${name} | CC Débarras 14`} />
                <meta property="og:description" content={metaDescription} />
                <meta property="og:url" content={canonicalUrl} />
                <meta property="og:type" content="website" />
                <meta property="og:image" content="https://ccdebarras14.fr/img/home/logo.png" />

                <script type="application/ld+json">{JSON.stringify(localBusinessSchema())}</script>
                <script type="application/ld+json">{JSON.stringify(cityServiceSchema({ name, postalCode }, canonicalUrl))}</script>
                <script type="application/ld+json">{JSON.stringify(breadcrumbSchema([
                    { name: 'Accueil', path: '/' },
                    { name: `Débarras ${name}`, path: `/debarras-${slug}` },
                ]))}</script>
            </Helmet>

            <Header />

            <div className="city-page">
                <div className="city-page-content">

                    {/* Breadcrumb */}
                    <div className="city-page-breadcrumb">
                        <Link to="/">Accueil</Link>
                        <span> › </span>
                        <span>Débarras {name}</span>
                    </div>

                    {/* Hero Banner */}
                    <div className="city-page-hero-banner">
                        <div className="city-page-hero-logo">
                            <img
                                src="/img/home/logo.png"
                                alt="CC Débarras 14 Logo"
                                className="city-page-hero-logo-img"
                            />
                        </div>
                        <div className="city-page-hero-image">
                            <img
                                src="/img/home/cover.png"
                                alt={`Débarras professionnel à ${name}`}
                                className="city-page-hero-cover-img"
                            />
                            <div className="city-page-hero-overlay">
                                <h1>Débarras à {name}</h1>
                            </div>
                        </div>
                    </div>

                    {/* Présentation */}
                    <div className="city-page-presentation">
                        <p>{intro}</p>
                        <p><strong>Nous vous proposons de vous débarrasser gratuitement*</strong></p>
                        <p>ALORS, N'HÉSITEZ PLUS... APPELEZ NOUS !</p>
                        <div className="city-page-presentation-button-container">
                            <button
                                className="city-page-call-button"
                                onClick={() => window.open('tel:0767567254')}
                            >
                                <span>
                                    <svg width="30" height="30" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" stroke="#ffffff">
                                        <path opacity="0.5" d="M15.5562 14.5477L15.1007 15.0272C15.1007 15.0272 14.0181 16.167 11.0631 13.0559C8.10812 9.94484 9.1907 8.80507 9.1907 8.80507L9.47752 8.50311C10.1841 7.75924 10.2507 6.56497 9.63424 5.6931L8.37326 3.90961C7.61028 2.8305 6.13596 2.68795 5.26145 3.60864L3.69185 5.26114C3.25823 5.71766 2.96765 6.30945 3.00289 6.96594C3.09304 8.64546 3.81071 12.259 7.81536 16.4752C12.0621 20.9462 16.0468 21.1239 17.6763 20.9631C18.1917 20.9122 18.6399 20.6343 19.0011 20.254L20.4217 18.7584C21.3806 17.7489 21.1102 16.0182 19.8833 15.312L17.9728 14.2123C17.1672 13.7486 16.1858 13.8848 15.5562 14.5477Z" fill="#ffffff"/>
                                        <path d="M13.2595 1.87983C13.3257 1.47094 13.7122 1.19357 14.1211 1.25976C14.4749 1.28933 14.6233 1.38082 14.9201 1.46726C15.3347 1.60967 15.8323 1.8378 16.8286 2.29456C18.1544 3.09356 19.5302 4.46936 20.906 5.84516C21.705 7.17097 22.1617 8.16725 22.3899 8.66487C22.6187 9.37625 22.7102 9.72901 22.7373 9.86245C22.8034 10.2713 22.5286 10.6739 22.1197 10.7401C21.712 10.8061 21.3279 10.53 21.2601 10.1231C21.2337 9.9997 21.2124 9.91188 21.1786 9.79572C21.1109 9.56339 20.9934 9.21806 20.7982 8.79238C20.4084 7.94207 19.7074 6.76789 18.4695 5.53002C17.2317 4.29216 16.0575 3.59117 15.2072 3.20134C14.7815 3.00618 14.4362 2.88865 14.2038 2.82097C13.8863 2.7413 13.4793 2.67347 13.2595 1.87983Z" fill="#ffffff"/>
                                    </svg>
                                </span>
                                <span>07 67 56 72 54</span>
                            </button>
                        </div>
                        <div className="city-page-disclaimer">
                            *Si rien n'est vendable nous établissons un devis pour l'enlèvement de vos affaires que nous trierons avant dépôt en déchetterie.
                        </div>
                    </div>

                    {/* Services */}
                    <div className="city-page-services">
                        <h2>Nous débarrassons à {name} :</h2>
                        <div className="city-page-services-grid">
                            <div className="city-page-service-card">
                                <h3>🏠 Résidentiel</h3>
                                <ul>
                                    <li>Maisons et appartements</li>
                                    <li>Caves et greniers</li>
                                    <li>Résidences secondaires</li>
                                    <li>Sous-sols</li>
                                </ul>
                            </div>
                            <div className="city-page-service-card">
                                <h3>📦 Situations</h3>
                                <ul>
                                    <li>Succession et héritage</li>
                                    <li>Départ en maison de retraite</li>
                                    <li>Vente immobilière</li>
                                    <li>Simple rangement</li>
                                </ul>
                            </div>
                            <div className="city-page-service-card">
                                <h3>🏢 Professionnel</h3>
                                <ul>
                                    <li>Bureaux et locaux</li>
                                    <li>Garages et entrepôts</li>
                                    <li>Hangars et combles</li>
                                    <li>Débarras divers</li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* Carousel */}
                    <div className="city-page-carousel-section">
                        <h2>Nos derniers débarras</h2>
                        <div className="city-page-carousel-container">
                            <div className="city-page-carousel-wrapper">
                                <button className="city-page-carousel-button" onClick={prevSlide}>❮</button>
                                <div className="city-page-carousel-content">
                                    <img
                                        src={carouselImages[currentSlide].src}
                                        alt={carouselImages[currentSlide].alt}
                                        className="city-page-carousel-image"
                                    />
                                    <div className="city-page-carousel-title">
                                        {carouselImages[currentSlide].title}
                                    </div>
                                </div>
                                <button className="city-page-carousel-button" onClick={nextSlide}>❯</button>
                            </div>
                            <div className="city-page-carousel-indicators">
                                {carouselImages.map((_, index) => (
                                    <button
                                        key={index}
                                        className={`city-page-carousel-indicator ${index === currentSlide ? 'active' : ''}`}
                                        onClick={() => setCurrentSlide(index)}
                                    />
                                ))}
                            </div>
                        </div>
                        <div className="city-page-see-all-link-container">
                            <Link to="/avant-apres" className="city-page-see-all-link">
                                Voir tous les débarras →
                            </Link>
                        </div>
                    </div>

                    <GoogleReviews />

                    {/* Débarras gratuit + zone d'intervention */}
                    <div className="city-page-info">
                        <div className="city-page-info-column">
                            <h2>Débarras gratuit à {name} : comment ça marche ?</h2>
                            <p>
                                Lorsque les objets récupérés lors du débarras ont une valeur suffisante pour être revendus,
                                nous intervenons <strong>gratuitement</strong>. Nous trions, chargeons et évacuons tout,
                                puis revendons les objets sur Leboncoin et Facebook Marketplace.
                            </p>
                            <p>
                                Si rien n'est vendable, nous établissons un devis clair pour l'enlèvement et le dépôt
                                en déchetterie. Pas de mauvaise surprise.
                            </p>
                        </div>

                        <div className="city-page-info-column">
                            <h2>Zone d'intervention autour de {name}</h2>
                            <p>Nous intervenons à {name} et dans les communes voisines :</p>
                            <ul className="city-page-nearby">
                                {nearby.map((city) => {
                                    const slug = citySlugMap[city];
                                    return (
                                        <li key={city}>
                                            {slug !== undefined
                                                ? <Link to={`/${slug}`}>{city}</Link>
                                                : <span>{city}</span>
                                            }
                                        </li>
                                    );
                                })}
                            </ul>
                            <p>
                                Vous avez un doute sur votre commune&nbsp;?&nbsp;
                                <Link to="/contact">Contactez-nous</Link>, nous répondrons rapidement.
                            </p>
                        </div>
                    </div>

                    <Faq title={`Débarras à ${name} : vos questions`} items={faqItems} />

                    {/* Navigation */}
                    <div className="city-page-links">
                        <Link to="/" className="city-page-link-secondary">← Retour à l'accueil</Link>
                        <Link to="/avant-apres" className="city-page-link-primary">Voir nos réalisations →</Link>
                    </div>

                </div>
            </div>

            <Footer />
        </>
    );
};

export default CityPage;
